import React, { FC, RefObject, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MyAccountContainer } from '@components/organisms/MyAccountContainer';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { Table } from '@components/atoms/Table';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { Pagination } from '@components/molecules/Pagination';
import { Card } from '@components/atoms/Card';
import { PopupDelete } from '@components/molecules/PopupDelete';
import { SITE_URLS } from '@constants';
import { usePermissionChecker, UserPermissions } from '@hooks/useHasPermission';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { useData } from '@hooks/useData';
import { useInfiniteScroll } from '@hooks/useInfiniteScroll';
import { useNotifications } from '@hooks/useNotifications';
import { useDeleteWorker } from '@services/useDeleteWorker';
import { ResultsType } from '@types';
import { useMediaQuery } from '@hooks/useMediaQuery';
import { useGetMyAccountWorkers } from '@services/useGetMyAccountWorkers';
import { useFilters } from '@hooks/useFilters';
import { Form } from '@components/atoms/Form';
import { Input, InputTypes, InputVariants } from '@components/atoms/Input';
import { useParams } from 'react-router';
import { WorkerStateBadge } from '@components/molecules/badges/WorkerStateBadge';

const TEXT_FILTER_ID = 'text-filter';

export const ManageWorkers: FC = () => {
    const { navigate } = useCustomNavigate();
    const { can } = usePermissionChecker();
    const { pageToRequest, resetPageToRequest, checkPageToRequest, observerRef: scrollObserverRef } = useInfiniteScroll();
    const { t } = useTranslation();

    const translations = {
        title: t('manage-workers-component.title'),
    };

    // if user does not have permission accessMerchantAreas, he can't access this page
    useEffect(() => {
        if (can(UserPermissions.accessMerchantAreas) === false) {
            navigate(`/${SITE_URLS.myProfile}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const formRef = useRef<HTMLFormElement>(null);

    return (
        <MyAccountContainer title={translations.title}>
            <>
                <div className="pt-4 pb-2">
                    <Filters formRef={formRef} pageToRequest={pageToRequest} checkPageToRequest={checkPageToRequest} />
                </div>
                <div className="pt-4">
                    <ManagementTable formRef={formRef} resetPageToRequest={resetPageToRequest} />
                    <div ref={scrollObserverRef} style={{ height: '1px' }} />
                </div>
            </>
        </MyAccountContainer>
    );
};

interface FiltersProps {
    formRef: RefObject<HTMLFormElement>;
    pageToRequest: number;
    checkPageToRequest: () => void;
}

const Filters: FC<FiltersProps> = ({ formRef, pageToRequest, checkPageToRequest }) => {
    const { store } = useParams();
    const pageSize = useMediaQuery();
    const { request: requestMyAccountWorkers } = useGetMyAccountWorkers();
    const { myAccountWorkers, isLoadingMyAccountWorkers, setMyAccountWorkers } = useData();
    const { appliedFilters, results, setResults, currentPage, setCurrentPage } = useFilters();
    const { t } = useTranslation();

    const translations = {
        addWorker: t('manage-workers-component.add-worker'),
    };

    useEffect(() => {
        // when unmounting page, reset results
        // to prevent showing old results when returning to this page
        return () => {
            setResults([]);
            setMyAccountWorkers(null);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getTextFilter = appliedFilters.find((item) => item.label === TEXT_FILTER_ID)?.values[0].name;

    const fetchWorkers = (page: number) => {
        setCurrentPage(page);

        // prevent calls for pages that don't exist
        if (myAccountWorkers?.pagination?.total_pages && page > myAccountWorkers?.pagination?.total_pages) {
            return;
        }

        requestMyAccountWorkers({
            textSearch: getTextFilter,
            uuid: store,
            page: page,
        });
    };

    // request more workers from next page
    useEffect(() => {
        pageToRequest > currentPage && !isLoadingMyAccountWorkers && fetchWorkers(currentPage + 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageToRequest, isLoadingMyAccountWorkers]);

    // request first page whenever a filter changes
    const submitForm = () => fetchWorkers(1);

    useEffect(() => {
        !isLoadingMyAccountWorkers && formRef?.current?.requestSubmit();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appliedFilters]);

    // every time the endpoint is called, append the data to results constant
    // if the page being called is the first page, reset results
    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setResults((previous: any[]) => {
            return myAccountWorkers?.pagination?.current_page === 1
                ? myAccountWorkers?.data || []
                : [...previous, ...(myAccountWorkers?.data || [])];
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [myAccountWorkers]);

    // manually check if next page should be requested
    // in case user's screen is big and isn't possible to scroll to trigger next page request
    useEffect(() => {
        checkPageToRequest();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [results.length]);

    return (
        <MyAccountContainer.Filters>
            <Form formRef={formRef} onSubmit={submitForm}>
                <TextFilter />
                {/* add worker */}
                <div>
                    <Anchor
                        href={SITE_URLS.myWorkersCreate.replace(':store', `${store}`)}
                        classes={['xs', 'sm'].includes(pageSize) ? 'w-100' : ''}
                        mode={AnchorMode.primary}>
                        {translations.addWorker}
                    </Anchor>
                </div>
            </Form>
        </MyAccountContainer.Filters>
    );
};

interface ManagementTableProps {
    formRef: RefObject<HTMLFormElement>;
    resetPageToRequest: () => void;
}

const ManagementTable: FC<ManagementTableProps> = ({ formRef, resetPageToRequest }) => {
    const { showDefaultNotification } = useNotifications();
    const {
        request: requestDelete,
        loading: loadingDelete,
        error: errorDelete,
        success: successDelete,
        lastResponseTime,
    } = useDeleteWorker();
    const { results: workers } = useFilters();
    const { isLoadingMyAccountWorkers } = useData();
    const { t } = useTranslation();

    const translations = {
        name: t('manage-workers-component.table.name'),
        condition: t('manage-workers-component.table.condition'),
        actions: t('manage-workers-component.table.actions'),
        deletePopupTitle: t('manage-workers-component.delete-popup.title'),
        deletePopupDescription: t('manage-workers-component.delete-popup.description'),
        deletePopupWarning: t('manage-workers-component.delete-popup.warning'),
    };

    // uuid of worker being removed
    const [showDeleteModal, setShowDeleteModal] = useState<string | null>(null);

    // dispatch notification, according to feedback of service
    useEffect(() => {
        if (lastResponseTime) {
            // close delete modal
            showDeleteModal !== null && setShowDeleteModal(null);

            if (errorDelete) {
                showDefaultNotification({ type: 'error', message: errorDelete });
            } else {
                if (successDelete) {
                    showDefaultNotification({ type: 'success', message: successDelete });
                }
                // reset paginated list of workers
                formRef?.current?.requestSubmit();
                resetPageToRequest();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResponseTime]);

    const handleOnConfirmDelete: () => void = () => {
        showDeleteModal && requestDelete({ uuid: showDeleteModal });
    };

    return (
        <div data-testid="management-table">
            <Card classes="mb-0">
                <Table
                    borderless={true}
                    headerCols={[
                        { classes: 'text-left', text: translations.name },
                        { classes: 'text-center', text: translations.condition },
                        { classes: 'text-end', fit: true, text: translations.actions },
                    ]}
                    contentRows={workers.map((item) => {
                        return [
                            {
                                classes: 'text-left',
                                content: <Typography fontSize="14">{item.name}</Typography>,
                            },
                            {
                                classes: 'text-center',
                                content: <WorkerStateBadge state={item.status || ''} />,
                            },
                            {
                                classes: 'text-end',
                                content: (
                                    <Table.Options
                                        options={[
                                            <Anchor
                                                key="option-1"
                                                href={SITE_URLS.myWorkersEdit.replace(':uuid', `${item.uuid}`)}
                                                mode={AnchorMode.none}>
                                                <Typography fontSize="16" tagVariant={TypographyTagVariants.span}>
                                                    <i className="fa-light fa-pen-to-square" />
                                                </Typography>
                                            </Anchor>,
                                            <button
                                                key="option-2"
                                                disabled={loadingDelete}
                                                onClick={() => setShowDeleteModal(item.uuid || null)}>
                                                <Typography fontSize="16" tagVariant={TypographyTagVariants.span}>
                                                    <i className="fa-light fa-trash-can" />
                                                </Typography>
                                            </button>,
                                        ]}
                                    />
                                ),
                            },
                        ];
                    })}
                />
            </Card>
            <Pagination loading={isLoadingMyAccountWorkers} type={ResultsType.workers} />
            <PopupDelete
                question={translations.deletePopupTitle}
                confirmation={translations.deletePopupDescription}
                warning={translations.deletePopupWarning}
                onClickConfirm={handleOnConfirmDelete}
                show={showDeleteModal !== null}
                onHide={() => setShowDeleteModal(null)}
            />
        </div>
    );
};

const TextFilter: FC = () => {
    const pageSize = useMediaQuery();
    const { appliedFilters, setFilterValues } = useFilters();
    const { t } = useTranslation();

    const translations = {
        searchPlaceholder: t('manage-workers-component.search.placeholder'),
    };

    return (
        <div className="align-content-center" style={{ width: ['xs', 'sm', 'md'].includes(pageSize) ? '100%' : 'unset' }}>
            <Input
                type={InputTypes.text}
                variant={InputVariants.searchInput}
                placeholder={translations.searchPlaceholder}
                value={appliedFilters.find((item) => item.label === TEXT_FILTER_ID)?.values[0].name || ''}
                onChange={(value) => setFilterValues(TEXT_FILTER_ID, [{ uuid: null, name: value }])}
            />
        </div>
    );
};
